import { Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '@ion/env';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  JWT,
  resetPassword,
  signIn,
  SignInOutput,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';

import { AppUser } from './app-user';
// 20 mins
const REFRESH_INTERVAL = 20 * 60 * 1000;

/** Messages of the errors the sign-in methods throw when Cognito needs one more step. */
export const SIGN_IN_ERROR = {
  NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
  EMAIL_CODE_REQUIRED: 'EMAIL_CODE_REQUIRED',
  INVALID_EMAIL_CODE: 'INVALID_EMAIL_CODE',
} as const;

/**
 * Authentication service that provides methods for signing in, signing out,
 * and managing user authentication using AWS Cognito.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
  /**
   * Interval for periodic token refresh.
   */
  private readonly interval = setInterval(() => this.refresh(), REFRESH_INTERVAL);

  /**
   * Last thrown error of AWS Cognito sign-in process, if there was any
   */
  lastAuthError = '';
  readonly isNewPasswordRequired = signal(false);
  /** E-mail address the sign-in code was sent to, set while Cognito waits for the code. */
  readonly emailCodeRecipient = signal<string | null>(null);
  private signInEmail = '';

  /**
   * Sign in a user using federated authentication.
   *
   * @param returnUrl - The return URL to redirect the user after authentication.
   * @returns A promise that resolves when the sign-in is successful.
   */
  async signIn(returnUrl: string): Promise<void> {
    return signInWithRedirect({
      provider: { custom: environment.ssoProviderName },
      customState: returnUrl,
    });
  }

  /**
   * Sign in a user using basic authentication via cognito.
   *
   * @param withEmailCode - Use the custom auth flow, the lambda of the user pool then asks for the e-mailed code
   * when the user has MFA enabled.
   * @returns A promise that resolves when the sign-in is successful.
   */
  async signInSubtenant(email: string, password: string, withEmailCode = false): Promise<void> {
    this.emailCodeRecipient.set(null);
    this.signInEmail = email;
    const result = await signIn({
      username: email,
      password: password,
      options: withEmailCode ? { authFlowType: 'CUSTOM_WITH_SRP' } : undefined,
    });

    this.checkNextStep(result.nextStep);
  }

  /**
   * Confirm new password for the user.
   *
   * @param user The user object containing user details.
   * @param password The new password to set for the user.
   */
  async confirmNewPassword(password: string): Promise<void> {
    const result = await confirmSignIn({
      challengeResponse: password,
    });
    this.isNewPasswordRequired.set(false);

    this.checkNextStep(result.nextStep);
  }

  /**
   * Answer the custom challenge with the code the user got by e-mail.
   *
   * @param code The code from the e-mail.
   */
  async confirmEmailCode(code: string): Promise<void> {
    const result = await confirmSignIn({
      challengeResponse: code,
    });

    // a wrong code gets the same challenge again, Cognito ends the sign-in after the last try
    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      throw new Error(SIGN_IN_ERROR.INVALID_EMAIL_CODE);
    }
    if (result.nextStep.signInStep !== 'DONE') {
      throw new Error(`Unexpected sign-in step ${result.nextStep.signInStep}`);
    }
    this.emailCodeRecipient.set(null);
  }

  private checkNextStep(nextStep: SignInOutput['nextStep']): void {
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      this.isNewPasswordRequired.set(true);
      throw new Error(SIGN_IN_ERROR.NEW_PASSWORD_REQUIRED);
    }

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      this.emailCodeRecipient.set(nextStep.additionalInfo?.['email'] || this.signInEmail);
      throw new Error(SIGN_IN_ERROR.EMAIL_CODE_REQUIRED);
    }
  }

  /**
   * Change the password for the current user.
   *
   * @param oldPassword  old password of current user
   * @param newPassword  new password of current user
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword, newPassword });
  }

  /**
   * Check if the user is currently signed in.
   *
   * @returns A promise that resolves to true if the user is signed in, otherwise false.
   */
  async isSignedIn(): Promise<boolean> {
    // we assume we are logged in when Cognito is disabled locally
    if (!environment.enableAuthentication) {
      return true;
    }
    try {
      // checks both user & session
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch {
      // cognito throws if there is no user
      return false;
    }
  }

  /**
   * Request a password reset for the given email address.
   *
   * @param email - The email address of the user requesting a password reset.
   * @returns A promise that resolves when the password reset request is successful.
   */
  async forgotPassword(email: string): Promise<void> {
    await resetPassword({ username: email });
  }

  /**
   * Confirm the password reset with the provided code and new password.
   *
   * @param username  - The email address of the user.
   * @param code - Secure code obtaied from email.
   * @param newPassword - New password.
   */
  async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username, newPassword, confirmationCode: code });
  }

  /**
   * Get the current user's information.
   *
   * @returns A promise that resolves to an object with user ID and email.
   */
  async currentUserInfo(): Promise<AppUser> {
    const [user, attributes] = await Promise.all([getCurrentUser(), fetchUserAttributes()]);

    if (!attributes.email) {
      throw new Error("Can't get email of current user");
    }

    return {
      id: user.userId,
      email: attributes.email,
      firstName: attributes.given_name,
      lastName: attributes.family_name,
      isCognitoUser: attributes['custom:azure_role'] === undefined,
      subtenantId: await this.getSubtenantId('custom:rkc_subtenant_id'),
    };
  }

  private async getSubtenantId(attributeName: string | undefined): Promise<string | undefined> {
    const jwt = await this.getIdToken();
    if (!jwt || !attributeName) {
      return undefined;
    }

    const payloadValue = jwt.payload[attributeName];
    if (typeof payloadValue !== 'string') {
      return undefined;
    }

    return payloadValue.split(';')[0]?.trim();
  }

  /**
   * Refresh the current session's tokens.
   */
  async refresh(): Promise<void> {
    try {
      // The fetchAuthSession API automatically refreshes the user's session when the authentication tokens have expired
      // and a valid refreshToken is present.
      await fetchAuthSession();
    } catch {
      // expected, cognito throws if we are not logged in
    }
  }

  /**
   * Sign out the currently authenticated user.
   */
  async signOut(): Promise<void> {
    await signOut();
  }

  getLastError(): string {
    return this.lastAuthError;
  }

  setLastError(error: string): void {
    this.lastAuthError = error;
  }

  /**
   * Get the authentication token for the current session.
   *
   * @returns The authentication token or null if there is no valid session.
   */
  async getToken(): Promise<string | null> {
    // cognito throws if there is not any session
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        return null;
      }
      // AWS recommends against using the idToken for authorization purposes. The accessToken should be used instead.
      // But because of how our authorization is set up, we need to use the idToken.
      // See: https://ionity.atlassian.net/wiki/spaces/IONIT/pages/252248558/JWT+structure+alignment
      return `Bearer ${session.tokens.idToken.toString()}`;
    } catch {
      return '';
    }
  }

  /**
   * Get the current ID JWT token of the user.
   *
   * @returns The ID JWT token or null if there is none or the session is not valid.
   */
  async getIdToken(): Promise<JWT | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Clean up resources when the component is destroyed.
   */
  ngOnDestroy(): void {
    clearInterval(this.interval);
  }
}
